// MultiSessionList.js
// -----------------------------------------------------------------------------
// The MultiCCS landing screen: every block the customer has ever started, split
// into In Progress and Completed. Sessions are never deleted — an abandoned
// block just stays in the In Progress tab until it's finished.
//
// All data lives in MultiCcsApp; this renders it and reports clicks back.
//
// Props:
//   sessions       array of normalized sessions              (required)
//   loading        bool                                      (required)
//   error          string | null                             (required)
//   onNewBlock     () => void                                (required)
//   onOpenSession  (session) => void  — resume or review     (required)
//   onRenameSession(session, title) => void                  (required)
//   onRetry        () => void                                (optional)
//   onExit         () => void — leave MultiCCS               (optional)
//   renamingId     number | null — save in flight            (optional)
// -----------------------------------------------------------------------------

import React from "react";
import { completedCount, formatElapsed } from "./multiCcsFetches";

function formatDate(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return (
    MONTHS[date.getMonth()] + " " + date.getDate() + ", " + date.getFullYear()
  );
}

// Total time spent across the finished cases in a block.
function totalElapsed(session) {
  let total = 0;
  let any = false;
  session.CaseProgress.Cases.forEach(function (entry) {
    if (typeof entry.ElapsedSeconds === "number") {
      total += entry.ElapsedSeconds;
      any = true;
    }
  });
  return any ? total : null;
}

class MultiSessionList extends React.Component {
  state = {
    tab: "active", // "active" | "done"
    editingId: null,
    draftTitle: "",
  };

  startRename = (session, e) => {
    e.stopPropagation();
    this.setState({ editingId: session.ID, draftTitle: session.Title });
  };

  commitRename = (session) => {
    const title = this.state.draftTitle.trim();
    this.setState({ editingId: null, draftTitle: "" });
    // An empty box or an unchanged title isn't a rename.
    if (!title || title === session.Title) return;
    this.props.onRenameSession(session, title);
  };

  cancelRename = () => {
    this.setState({ editingId: null, draftTitle: "" });
  };

  handleRenameKey = (session, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      this.commitRename(session);
    } else if (e.key === "Escape") {
      e.preventDefault();
      this.cancelRename();
    }
  };

  renderRow(session) {
    const done = completedCount(session.CaseProgress);
    const total = session.CasesList.length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    const created = formatDate(session.CaseProgress.CreatedDate);
    const elapsed = totalElapsed(session);
    const isEditing = this.state.editingId === session.ID;
    const isRenaming = this.props.renamingId === session.ID;

    const meta = [
      total + (total === 1 ? " case" : " cases"),
      done + " of " + total + " complete",
    ];
    if (created) meta.push(created);
    if (session.Completed && elapsed !== null) {
      meta.push(formatElapsed(elapsed) + " total");
    }

    return (
      <div className="mccs-session" key={session.ID}>
        <div className="mccs-session__main">
          {isEditing ? (
            <input
              className="mccs-input"
              type="text"
              autoFocus
              maxLength={120}
              aria-label="Block name"
              value={this.state.draftTitle}
              onChange={(e) => {
                this.setState({ draftTitle: e.target.value });
              }}
              onKeyDown={(e) => {
                this.handleRenameKey(session, e);
              }}
              onBlur={() => {
                this.commitRename(session);
              }}
            />
          ) : (
            <h2 className="mccs-session__title">
              {session.Title}
              {isRenaming && (
                <span className="mccs-hint"> &nbsp;Saving...</span>
              )}
            </h2>
          )}
          <p className="mccs-session__meta">{meta.join(" \u00b7 ")}</p>
          <div
            className="mccs-progress"
            role="progressbar"
            aria-valuenow={done}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-label={done + " of " + total + " cases complete"}
          >
            <div className="mccs-progress__fill" style={{ width: pct + "%" }} />
          </div>
        </div>
        <div className="mccs-session__actions">
          {!isEditing && (
            <button
              type="button"
              className="mccs-btn mccs-btn--sm"
              onClick={(e) => {
                this.startRename(session, e);
              }}
            >
              Rename
            </button>
          )}
          <button
            type="button"
            className="mccs-btn mccs-btn--sm mccs-btn--primary"
            onClick={() => {
              this.props.onOpenSession(session);
            }}
          >
            {session.Completed ? "Review" : "Resume"}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { sessions, loading, error, onNewBlock, onRetry, onExit } =
      this.props;
    const { tab } = this.state;

    if (loading) {
      return (
        <div className="mccs-scroll">
          <div className="mccs-page">
            <div className="mccs-loading">Loading your blocks...</div>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="mccs-scroll">
          <div className="mccs-page">
            <div className="mccs-error">
              <p>{error}</p>
              {onRetry && (
                <button type="button" className="mccs-btn" onClick={onRetry}>
                  Try again
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    const active = sessions.filter(function (session) {
      return !session.Completed;
    });
    const done = sessions.filter(function (session) {
      return session.Completed;
    });
    const shown = tab === "active" ? active : done;

    return (
      <div className="mccs-scroll">
        <div className="mccs-page">
          <div className="mccs-head">
            <h1 className="mccs-title">
              <small>Multi CCS</small>
              Your blocks
            </h1>
            <div className="mccs-head-actions">
              {onExit && (
                <button type="button" className="mccs-btn" onClick={onExit}>
                  Back to CCS cases
                </button>
              )}
              <button
                type="button"
                className="mccs-btn mccs-btn--primary"
                onClick={onNewBlock}
              >
                New block
              </button>
            </div>
          </div>

          <div className="mccs-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "active"}
              className={"mccs-tab" + (tab === "active" ? " mccs-tab--on" : "")}
              onClick={() => {
                this.setState({ tab: "active" });
              }}
            >
              In progress
              <span className="mccs-tab-count">{active.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "done"}
              className={"mccs-tab" + (tab === "done" ? " mccs-tab--on" : "")}
              onClick={() => {
                this.setState({ tab: "done" });
              }}
            >
              Completed
              <span className="mccs-tab-count">{done.length}</span>
            </button>
          </div>

          {shown.length === 0 ? (
            <div className="mccs-empty">
              {tab === "active" ? (
                <>
                  <h2>No blocks in progress</h2>
                  <p>
                    A block runs several cases back to back, the way the real
                    exam does. Pick up to 13 cases and work through them one
                    after another, with a break screen in between.
                  </p>
                  <button
                    type="button"
                    className="mccs-btn mccs-btn--primary"
                    onClick={onNewBlock}
                  >
                    Build your first block
                  </button>
                </>
              ) : (
                <>
                  <h2>Nothing finished yet</h2>
                  <p>
                    Finish every case in a block and it moves here, where you
                    can go through the grades case by case.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="mccs-sessions">
              {shown.map((session) => {
                return this.renderRow(session);
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default MultiSessionList;